import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Layout from "../components/Layout";
import api from "../lib/api";
import ScoreBadge from "../components/ScoreBadge";
import LeadDrawer from "../components/LeadDrawer";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Search, Sparkles, Plus } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { QuickAddModal, BulkImportModal } from "../components/EnrichmentModals";
import { CsvUploadModal } from "../components/CsvUploadModal";

const INDUSTRIES = ["Manufacturing", "Logistics", "Construction", "Retail", "Aged Care", "Distribution", "Multi-site Operations"];

function NewLeadModal({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    full_name: "", title: "CFO", email: "", company: "", industry: "Manufacturing",
    employees: "", revenue: "", linkedin_url: "", website: "", multi_site: true,
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, employees: form.employees ? Number(form.employees) : null };
      const { data } = await api.post("/leads", payload);
      toast.success("Lead created");
      onCreated(data);
      setOpen(false);
      setForm({ full_name: "", title: "CFO", email: "", company: "", industry: "Manufacturing", employees: "", revenue: "", linkedin_url: "", website: "", multi_site: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create lead");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" data-testid="new-lead-btn" style={{ borderRadius: 2 }}>
          <Plus className="h-4 w-4 mr-1" strokeWidth={1.5} /> Manual
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg" style={{ borderRadius: 2 }}>
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl tracking-tight" style={{ fontWeight: 400 }}>New lead</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4" data-testid="new-lead-form">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="overline">Full name</Label>
              <Input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} required className="mt-1 input-crisp" data-testid="new-lead-name" />
            </div>
            <div>
              <Label className="overline">Title</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1 input-crisp" data-testid="new-lead-title" />
            </div>
          </div>
          <div>
            <Label className="overline">Email</Label>
            <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required className="mt-1 input-crisp" data-testid="new-lead-email" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="overline">Company</Label>
              <Input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} required className="mt-1 input-crisp" data-testid="new-lead-company" />
            </div>
            <div>
              <Label className="overline">Industry</Label>
              <Select value={form.industry} onValueChange={(v) => setForm({ ...form, industry: v })}>
                <SelectTrigger className="mt-1 input-crisp" data-testid="new-lead-industry"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {INDUSTRIES.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="overline">Employees</Label>
              <Input type="number" value={form.employees} onChange={(e) => setForm({ ...form, employees: e.target.value })} className="mt-1 input-crisp" />
            </div>
            <div>
              <Label className="overline">Revenue band</Label>
              <Input value={form.revenue} onChange={(e) => setForm({ ...form, revenue: e.target.value })} placeholder="$80M-$120M" className="mt-1 input-crisp" />
            </div>
          </div>
          <div className="flex items-center justify-between border-t border-border pt-3">
            <Label htmlFor="multi" className="text-sm">Multi-site operator</Label>
            <Switch id="multi" checked={form.multi_site} onCheckedChange={(v) => setForm({ ...form, multi_site: v })} data-testid="new-lead-multisite" />
          </div>
          <DialogFooter>
            <Button type="submit" disabled={saving} data-testid="new-lead-submit" style={{ borderRadius: 2 }}>{saving ? "Saving…" : "Create lead"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function Leads() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [band, setBand] = useState("all");
  const [industry, setIndustry] = useState("all");
  const [focusLead, setFocusLead] = useState(null);
  const [params] = useSearchParams();

  // Honour ?filter=bounced (or ?filter=high/medium/nurture) deep-links from
  // dashboard widgets so users land directly on the right slice.
  useEffect(() => {
    const f = params.get("filter");
    if (f && ["bounced", "high", "medium", "nurture", "all"].includes(f)) {
      setBand(f);
    }
  }, [params]);

  const load = async () => {
    setLoading(true);
    try {
      const query = {};
      if (band === "bounced") query.bounced = true;
      else if (band !== "all") query.band = band;
      if (industry !== "all") query.industry = industry;
      if (q) query.q = q;
      const { data } = await api.get("/leads", { params: query });
      setLeads(data);
    } catch {
      toast.error("Failed to load leads");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [band, industry]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q]);
  useEffect(() => {
    const focusId = params.get("focus");
    if (focusId && leads.length) {
      const l = leads.find((x) => x.lead_id === focusId);
      if (l) setFocusLead(l);
    }
  }, [params, leads]);

  const counts = useMemo(() => ({
    all: leads.length,
    high: leads.filter((l) => l.score_band === "high").length,
    medium: leads.filter((l) => l.score_band === "medium").length,
    nurture: leads.filter((l) => l.score_band === "nurture").length,
    bounced: leads.filter((l) => l.email_bounced === true).length,
  }), [leads]);

  return (
    <Layout
      title="Leads"
      subtitle="Database"
      actions={
        <>
          <CsvUploadModal onImported={load} />
          <BulkImportModal onCreated={load} />
          <QuickAddModal onCreated={(l) => setLeads((prev) => [l, ...prev])} />
          <NewLeadModal onCreated={(l) => setLeads((prev) => [l, ...prev])} />
        </>
      }
    >
      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <div className="relative flex-1 min-w-[260px] max-w-md">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" strokeWidth={1.5} />
          <Input
            placeholder="Search name, company, email…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="leads-search"
            className="pl-9 input-crisp"
          />
        </div>
        <div className="flex items-center gap-1 bg-card border border-border p-1" style={{ borderRadius: 2 }}>
          {[
            { v: "all", l: `All · ${counts.all}` },
            { v: "high", l: `High · ${counts.high}` },
            { v: "medium", l: `Medium · ${counts.medium}` },
            { v: "nurture", l: `Nurture · ${counts.nurture}` },
            { v: "bounced", l: `Bounced · ${counts.bounced}` },
          ].map((b) => (
            <button
              key={b.v}
              onClick={() => setBand(b.v)}
              data-testid={`filter-band-${b.v}`}
              className={`px-3 py-1.5 text-xs transition-colors ${band === b.v ? (b.v === "bounced" ? "bg-rose-600 text-white" : "bg-foreground text-background") : (b.v === "bounced" ? "text-rose-600 hover:text-rose-700" : "text-muted-foreground hover:text-foreground")}`}
              style={{ borderRadius: 2 }}
            >
              {b.l}
            </button>
          ))}
        </div>
        <Select value={industry} onValueChange={setIndustry}>
          <SelectTrigger className="w-[180px] input-crisp" data-testid="filter-industry"><SelectValue placeholder="Industry" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All industries</SelectItem>
            {INDUSTRIES.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <div className="bg-card border border-border" style={{ borderRadius: 2 }}>
        <div className="px-6 py-3 grid grid-cols-12 gap-4 border-b border-border">
          <div className="col-span-4 overline">Lead</div>
          <div className="col-span-3 overline">Company</div>
          <div className="col-span-2 overline">Industry</div>
          <div className="col-span-1 overline">Status</div>
          <div className="col-span-2 overline text-right">Score</div>
        </div>
        {loading && <div className="px-6 py-10 text-sm text-muted-foreground">Loading…</div>}
        {!loading && leads.length === 0 && (
          <div className="px-6 py-12 text-center">
            <Sparkles className="h-6 w-6 text-muted-foreground mx-auto mb-3" strokeWidth={1.5} />
            <div className="text-sm text-muted-foreground">No leads yet. Click "Load sample CFO leads" on the dashboard or "Add lead" above.</div>
          </div>
        )}
        {!loading && leads.map((l) => (
          <div
            key={l.lead_id}
            onClick={() => setFocusLead(l)}
            className="tbl-row px-6 py-4 grid grid-cols-12 gap-4 items-center cursor-pointer"
            data-testid={`lead-row-${l.lead_id}`}
          >
            <div className="col-span-4 flex items-center gap-3 min-w-0">
              <div className="h-9 w-9 bg-secondary text-foreground flex items-center justify-center text-sm font-medium shrink-0" style={{ borderRadius: 2 }}>
                {l.full_name?.[0]}
              </div>
              <div className="min-w-0">
                <div className="font-medium truncate">{l.full_name}</div>
                <div className="text-xs text-muted-foreground truncate">{l.title} · {l.email}</div>
              </div>
            </div>
            <div className="col-span-3 min-w-0">
              <div className="truncate">{l.company}</div>
              <div className="text-xs text-muted-foreground truncate">{l.employees ? `${l.employees} employees` : ""} {l.multi_site ? "· multi-site" : ""}</div>
            </div>
            <div className="col-span-2 text-sm text-muted-foreground truncate">{l.industry}</div>
            <div className="col-span-1 text-xs capitalize">{l.status?.replace(/_/g, " ")}</div>
            <div className="col-span-2 flex justify-end"><ScoreBadge score={l.score} band={l.score_band} /></div>
          </div>
        ))}
      </div>

      <LeadDrawer
        lead={focusLead}
        onClose={() => setFocusLead(null)}
        onUpdated={(updated) => {
          setFocusLead(updated);
          setLeads((prev) => prev.map((x) => (x.lead_id === updated.lead_id ? updated : x)));
        }}
      />
    </Layout>
  );
}
